import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  theme,
  Card,
  Typography,
  Button,
  Space,
  Switch,
  Input,
  InputNumber,
  Form,
  App,
  Spin,
  Row,
  Col,
  Alert,
  Divider,
} from 'antd';
import type { Rule } from 'antd/es/form';
import {
  SettingOutlined,
  SaveOutlined,
  ArrowLeftOutlined,
  InfoCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  APP_SETTINGS_VERSION_FIELDS,
  AppSettings,
  DEFAULT_UPDATE_NOTICE_INTERVAL_HOURS,
  MAX_UPDATE_NOTICE_INTERVAL_HOURS,
  UpdateAppSettingsRequest,
  compareSemver,
  isSemver,
} from '@mygate/common';
import { appSettingsService } from '@/services/appSettings';
import { useAuth } from '@/hooks/useAuth';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface AppSettingsFormValues {
  signupEnabled: boolean;
  signupDisabledMessage: string;
  tanganyOnboardingEnabled: boolean;
  onboardingMinVersion: string;
  promotionalConsentRequired: boolean;
  iosLatestVersion: string;
  iosMinVersion: string;
  androidLatestVersion: string;
  androidMinVersion: string;
  updateNoticeIntervalHours: number;
  updateNoticeOnLaunch: boolean;
  updateNoticeInCockpit: boolean;
}

const DEFAULT_SIGNUP_DISABLED_MESSAGE = 'New user registration is currently disabled. Please try again later.';

const SEMVER_OR_EMPTY_RULE: Rule = {
  validator(_rule, value: string | undefined) {
    return !value || isSemver(value)
      ? Promise.resolve()
      : Promise.reject(new Error('Must be in semver format (e.g., "2.5.0") or empty'));
  },
};

const BOOLEAN_FIELDS = [
  'signupEnabled',
  'tanganyOnboardingEnabled',
  'promotionalConsentRequired',
  'updateNoticeOnLaunch',
  'updateNoticeInCockpit',
] as const;

/**
 * Only the fields this admin actually changed. Sending the whole form would let a tab opened before another admin's
 * save quietly undo it, and for a version an empty field means "remove" (MYG-887).
 */
function changedSettings(saved: AppSettings, values: AppSettingsFormValues): UpdateAppSettingsRequest {
  const updates: UpdateAppSettingsRequest = {};
  for (const field of BOOLEAN_FIELDS) {
    if (values[field] !== saved[field]) {
      updates[field] = values[field];
    }
  }
  for (const field of APP_SETTINGS_VERSION_FIELDS) {
    // An empty string clears the version; `undefined` would be dropped from the JSON and keep the old value
    const value = values[field] || '';
    if (value !== (saved[field] || '')) {
      updates[field] = value;
    }
  }
  if (values.signupDisabledMessage !== (saved.signupDisabledMessage || DEFAULT_SIGNUP_DISABLED_MESSAGE)) {
    updates.signupDisabledMessage = values.signupDisabledMessage;
  }
  if (values.updateNoticeIntervalHours !== saved.updateNoticeIntervalHours) {
    updates.updateNoticeIntervalHours = values.updateNoticeIntervalHours;
  }
  return updates;
}

/**
 * MYG-887: a minimum version needs a store version to update to, and must not be above it. The backend checks the
 * same rule; checking here too keeps staff in the form instead of a failed save.
 */
function minVersionRule(latestField: 'iosLatestVersion' | 'androidLatestVersion'): Rule {
  return ({ getFieldValue }) => ({
    validator(_rule, value: string | undefined) {
      const latest: string | undefined = getFieldValue(latestField);
      if (!value || !isSemver(value)) {
        return Promise.resolve();
      }
      if (!latest) {
        return Promise.reject(new Error('Set the latest store version first'));
      }
      if (isSemver(latest) && compareSemver(value, latest) > 0) {
        return Promise.reject(new Error(`Must not be higher than the latest store version (${latest})`));
      }
      return Promise.resolve();
    },
  });
}

export function AppSettingsPage() {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const { staff, isAdmin } = useAuth();
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [tanganyEnabled, setTanganyEnabled] = useState(true);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await appSettingsService.getSettings();
      setSettings(data);
      setSignupEnabled(data.signupEnabled);
      setTanganyEnabled(data.tanganyOnboardingEnabled);
      form.setFieldsValue({
        signupEnabled: data.signupEnabled,
        signupDisabledMessage: data.signupDisabledMessage || DEFAULT_SIGNUP_DISABLED_MESSAGE,
        tanganyOnboardingEnabled: data.tanganyOnboardingEnabled,
        onboardingMinVersion: data.onboardingMinVersion || '',
        promotionalConsentRequired: data.promotionalConsentRequired,
        iosLatestVersion: data.iosLatestVersion || '',
        iosMinVersion: data.iosMinVersion || '',
        androidLatestVersion: data.androidLatestVersion || '',
        androidMinVersion: data.androidMinVersion || '',
        updateNoticeIntervalHours: data.updateNoticeIntervalHours,
        updateNoticeOnLaunch: data.updateNoticeOnLaunch,
        updateNoticeInCockpit: data.updateNoticeInCockpit,
      });
    } catch (error) {
      setLoadError(true);
      message.error('Failed to load app settings');
      console.error('Failed to load app settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values: AppSettingsFormValues) => {
    if (!settings) {
      return;
    }
    const updates = changedSettings(settings, values);
    if (Object.keys(updates).length === 0) {
      message.info('No changes to save');
      return;
    }
    try {
      setSaving(true);
      const updatedSettings = await appSettingsService.updateSettings(updates);
      setSettings(updatedSettings);
      setSignupEnabled(values.signupEnabled);
      setTanganyEnabled(values.tanganyOnboardingEnabled);
      message.success('App settings updated successfully');
    } catch (error) {
      message.error(
        error instanceof Error ? `Failed to update app settings: ${error.message}` : 'Failed to update app settings',
      );
      console.error('Failed to update app settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSignupToggle = (checked: boolean) => {
    setSignupEnabled(checked);
    form.setFieldValue('signupEnabled', checked);
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  if (!isAdmin) {
    return (
      <Card>
        <Alert
          type="error"
          message="Access Denied"
          description="Only administrators can access app settings"
          showIcon
          icon={<WarningOutlined />}
        />
        <Button type="primary" icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginTop: 16 }}>
          Back to Dashboard
        </Button>
      </Card>
    );
  }

  return (
    <>
      <div className="page-toolbar">
        <Typography.Text type="secondary">Registrierung und Onboarding konfigurieren.</Typography.Text>
      </div>

      <div style={{ maxWidth: 960 }}>
        {loading ? (
          <Card style={{ textAlign: 'center', padding: 48 }}>
            <Spin size="large" />
            <Text style={{ display: 'block', marginTop: 16 }}>Loading settings...</Text>
          </Card>
        ) : loadError ? (
          <Alert
            type="error"
            showIcon
            message="Einstellungen konnten nicht geladen werden"
            description="Bitte erneut laden, bevor du Änderungen vornimmst."
            action={<Button onClick={loadSettings}>Erneut laden</Button>}
          />
        ) : (
          <Card>
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              {/* Header Section */}
              <div>
                <Space wrap align="center" style={{ marginBottom: 8 }}>
                  <SettingOutlined style={{ fontSize: 24, color: token.colorPrimary }} />
                  <Title level={4} style={{ margin: 0 }}>
                    Global App Settings
                  </Title>
                </Space>
                <Typography.Paragraph type="secondary">
                  Configure global application settings that affect all users
                </Typography.Paragraph>
              </div>

              <Divider />

              {/* Last Updated Info */}
              {settings && settings.updatedAt && (
                <Alert
                  type="info"
                  message={
                    <Space wrap>
                      <InfoCircleOutlined />
                      <Text>
                        Last updated: {new Date(settings.updatedAt).toLocaleString()}
                        {settings.updatedBy && ` by ${settings.updatedBy}`}
                      </Text>
                    </Space>
                  }
                  showIcon={false}
                />
              )}

              {/* Settings Form */}
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                  signupEnabled: true,
                  signupDisabledMessage: DEFAULT_SIGNUP_DISABLED_MESSAGE,
                  tanganyOnboardingEnabled: true,
                  onboardingMinVersion: '',
                  promotionalConsentRequired: false,
                  iosLatestVersion: '',
                  iosMinVersion: '',
                  androidLatestVersion: '',
                  androidMinVersion: '',
                  updateNoticeIntervalHours: DEFAULT_UPDATE_NOTICE_INTERVAL_HOURS,
                  updateNoticeOnLaunch: true,
                  updateNoticeInCockpit: true,
                }}
              >
                {/* Signup Toggle */}
                <Card style={{ marginBottom: 24, backgroundColor: signupEnabled ? '#f6ffed' : '#fff1f0' }}>
                  <Row align="middle" justify="space-between" gutter={[16, 16]}>
                    <Col xs={24} md={18}>
                      <Space direction="vertical" size="small">
                        <Title level={5} style={{ margin: 0 }}>
                          New User Signups
                        </Title>
                        <Text type="secondary">Control whether new users can register through the mobile app</Text>
                        <Alert
                          type={signupEnabled ? 'success' : 'warning'}
                          message={
                            signupEnabled ? 'New users can currently sign up' : 'New user registration is disabled'
                          }
                          showIcon
                          style={{ marginTop: 8 }}
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Form.Item name="signupEnabled" valuePropName="checked" style={{ margin: 0 }}>
                        <Switch
                          checked={signupEnabled}
                          onChange={handleSignupToggle}
                          checkedChildren="Enabled"
                          unCheckedChildren="Disabled"
                          size="default"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                {/* Disabled Message */}
                {!signupEnabled && (
                  <Form.Item
                    label="Message for Users When Signup is Disabled"
                    name="signupDisabledMessage"
                    rules={[
                      {
                        required: true,
                        message: 'Please provide a message to show users',
                      },
                      {
                        max: 500,
                        message: 'Message cannot exceed 500 characters',
                      },
                    ]}
                    help="This message will be shown to users who try to sign up when registration is disabled"
                  >
                    <TextArea
                      rows={4}
                      placeholder="Enter a message to display when signup is disabled..."
                      showCount
                      maxLength={500}
                    />
                  </Form.Item>
                )}

                {/* Tangany Onboarding Toggle */}
                <Card style={{ marginBottom: 24, backgroundColor: tanganyEnabled ? '#f6ffed' : '#fff1f0' }}>
                  <Row align="middle" justify="space-between" gutter={[16, 16]}>
                    <Col xs={24} md={18}>
                      <Space direction="vertical" size="small">
                        <Title level={5} style={{ margin: 0 }}>
                          Tangany Onboarding
                        </Title>
                        <Text type="secondary">
                          Control whether users can start Tangany onboarding through the mobile app
                        </Text>
                        <Alert
                          type={tanganyEnabled ? 'success' : 'warning'}
                          message={tanganyEnabled ? 'Tangany onboarding is enabled' : 'Tangany onboarding is disabled'}
                          showIcon
                          style={{ marginTop: 8 }}
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Form.Item name="tanganyOnboardingEnabled" valuePropName="checked" style={{ margin: 0 }}>
                        <Switch
                          checked={tanganyEnabled}
                          onChange={checked => {
                            setTanganyEnabled(checked);
                            form.setFieldValue('tanganyOnboardingEnabled', checked);
                          }}
                          checkedChildren="Enabled"
                          unCheckedChildren="Disabled"
                          size="default"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                {/* Minimum App Version */}
                <Card style={{ marginBottom: 24 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Title level={5} style={{ margin: 0 }}>
                      Minimum App Version for Onboarding
                    </Title>
                    <Text type="secondary">
                      Users with app versions below this cannot start KYC. Leave empty for no restriction.
                    </Text>
                    <Form.Item
                      name="onboardingMinVersion"
                      rules={[SEMVER_OR_EMPTY_RULE]}
                      style={{ marginBottom: 0, marginTop: 8 }}
                    >
                      <Input placeholder="e.g., 2.5.0" style={{ maxWidth: 200 }} />
                    </Form.Item>
                  </Space>
                </Card>

                {/* MYG-887: store versions → optional update notice and mandatory update screen */}
                <Card style={{ marginBottom: 24 }}>
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <Title level={5} style={{ margin: 0 }}>
                      App Updates
                    </Title>
                    <Text type="secondary">
                      Users below the latest store version see a notice they can dismiss. Users below the minimum
                      version see a full-screen update screen and cannot use the app until they update. Leave a field
                      empty to turn that part off.
                    </Text>
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginTop: 8 }}
                      message="Enter a version only after the store offers it to everyone. App Review and a Google Play staged rollout can take days; until then the store still shows the old version."
                    />
                    <Alert
                      type="info"
                      showIcon
                      message="Only app releases that contain this feature (MYG-887) read these fields. Older releases ignore them."
                    />
                    {(['ios', 'android'] as const).map(platform => {
                      const latestField = platform === 'ios' ? 'iosLatestVersion' : 'androidLatestVersion';
                      const minField = platform === 'ios' ? 'iosMinVersion' : 'androidMinVersion';
                      const storeName = platform === 'ios' ? 'App Store' : 'Google Play';
                      return (
                        <Row key={platform} gutter={[16, 0]} style={{ marginTop: 16 }}>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name={latestField}
                              label={`${platform === 'ios' ? 'iOS' : 'Android'}: latest version in ${storeName}`}
                              rules={[SEMVER_OR_EMPTY_RULE]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="e.g., 2.7.4" style={{ maxWidth: 200 }} />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item
                              name={minField}
                              label={`${platform === 'ios' ? 'iOS' : 'Android'}: minimum supported version`}
                              dependencies={[latestField]}
                              rules={[SEMVER_OR_EMPTY_RULE, minVersionRule(latestField)]}
                              style={{ marginBottom: 0 }}
                            >
                              <Input placeholder="empty: nobody is blocked" style={{ maxWidth: 200 }} />
                            </Form.Item>
                          </Col>
                        </Row>
                      );
                    })}
                    <Divider style={{ margin: '16px 0 8px' }} />
                    <Form.Item
                      name="updateNoticeIntervalHours"
                      label="Show the optional notice again after (hours)"
                      extra="0: every time the user opens the app. Never twice while the app stays open. A new store version shows the notice at once."
                      rules={[{ required: true, message: 'Enter a number of hours' }]}
                    >
                      <InputNumber
                        min={0}
                        max={MAX_UPDATE_NOTICE_INTERVAL_HOURS}
                        precision={0}
                        style={{ width: 120 }}
                      />
                    </Form.Item>
                    <Row gutter={[16, 0]}>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="updateNoticeOnLaunch"
                          label="Popup when the user enters the app"
                          valuePropName="checked"
                          style={{ marginBottom: 0 }}
                        >
                          <Switch checkedChildren="On" unCheckedChildren="Off" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} md={12}>
                        <Form.Item
                          name="updateNoticeInCockpit"
                          label="Banner in the cockpit"
                          valuePropName="checked"
                          style={{ marginBottom: 0 }}
                        >
                          <Switch checkedChildren="On" unCheckedChildren="Off" />
                        </Form.Item>
                      </Col>
                    </Row>
                  </Space>
                </Card>

                {/* MYG-821: explicit consent to promotional pushes */}
                <Card style={{ marginBottom: 24 }}>
                  <Row align="middle" justify="space-between" gutter={[16, 16]}>
                    <Col xs={24} md={18}>
                      <Space direction="vertical" size="small">
                        <Title level={5} style={{ margin: 0 }}>
                          Promotional pushes need explicit consent
                        </Title>
                        <Text type="secondary">
                          When on, broadcasts and the automatic promotional notifications reach only users who saved
                          their notification settings in the app with promotional notifications left on. Everyone else
                          counts as not having agreed. Pushes sent by hand from the Notifications page are not affected.
                        </Text>
                        <Alert
                          type="warning"
                          showIcon
                          style={{ marginTop: 8 }}
                          message="Until the app asks for this consent, almost nobody has given it: the broadcast audience drops to the few users who saved their settings. The broadcast estimate shows how many that is."
                        />
                      </Space>
                    </Col>
                    <Col>
                      <Form.Item name="promotionalConsentRequired" valuePropName="checked" style={{ margin: 0 }}>
                        <Switch checkedChildren="Required" unCheckedChildren="Not required" />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>

                {/* Additional Information */}
                <Alert
                  type="info"
                  message="Important Information"
                  description={
                    <ul style={{ paddingLeft: 20, margin: '8px 0' }}>
                      <li>Existing users will still be able to log in when signup is disabled</li>
                      <li>Changes take effect immediately across all mobile apps</li>
                      <li>
                        This setting only affects the mobile app - staff can still create users through the backoffice
                      </li>
                      <li>Disabling Tangany onboarding skips crypto wallet provisioning after KYC</li>
                      <li>Minimum version check requires users to update their app before starting onboarding</li>
                    </ul>
                  }
                  showIcon
                />

                {/* Form Actions */}
                <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
                  <Space wrap>
                    <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saving} size="large">
                      Save Settings
                    </Button>
                    <Button onClick={loadSettings} disabled={saving}>
                      Reset
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </Space>
          </Card>
        )}

        {/* Current User Info */}
        <Card style={{ marginTop: 24 }}>
          <Space wrap>
            <InfoCircleOutlined />
            <Text type="secondary">
              Logged in as: {staff?.firstName} {staff?.lastName} ({staff?.role})
            </Text>
          </Space>
        </Card>
      </div>
    </>
  );
}

export default AppSettingsPage;
